import React from "react";
import { Link } from "react-router-dom";
import { useCompare } from "../context/CompareContext";
import { formatTT } from "../lib/api";
import { X, Scale } from "lucide-react";
import ApplyDialog from "../components/ApplyDialog";

const Compare = () => {
    const { items, remove, clear } = useCompare();

    if (items.length === 0) {
        return (
            <div className="max-w-3xl mx-auto px-6 py-20 text-center">
                <Scale className="w-12 h-12 mx-auto mb-4 text-[#718096]" strokeWidth={1.25}/>
                <h1 className="font-serif-display text-4xl mb-3">No products to compare</h1>
                <p className="text-sm text-[#4A5568] mb-6">Add up to 3 products from the catalog to see them side by side.</p>
                <Link to="/products" data-testid="compare-empty-browse" className="btn-primary px-6 py-3 label-overline">Browse Products</Link>
            </div>
        );
    }

    const isLoan = items[0].type === "loan";

    const rows = isLoan ? [
        { label: "Provider", fn: (p) => p.provider },
        { label: "Category", fn: (p) => p.category },
        { label: "Interest Rate", fn: (p) => `${p.interest_rate}%`, best: (vals) => vals.indexOf(Math.min(...vals.map(v => parseFloat(v)))) },
        { label: "Min Amount", fn: (p) => formatTT(p.min_amount) },
        { label: "Max Amount", fn: (p) => formatTT(p.max_amount) },
        { label: "Min Tenure", fn: (p) => `${p.min_tenure_months} mo` },
        { label: "Max Tenure", fn: (p) => `${p.max_tenure_months} mo` },
        { label: "Processing Fee", fn: (p) => `${p.processing_fee_pct}%`, best: (vals) => vals.indexOf(Math.min(...vals.map(v => parseFloat(v)))) },
        { label: "Min Income", fn: (p) => formatTT(p.min_income) },
        { label: "Min Credit Score", fn: (p) => p.min_credit_score },
        { label: "Rating", fn: (p) => `${p.rating}/5` },
        { label: "Eligibility", fn: (p) => p.eligibility, multiline: true },
    ] : [
        { label: "Provider", fn: (p) => p.provider },
        { label: "APR", fn: (p) => `${p.interest_rate}%`, best: (vals) => vals.indexOf(Math.min(...vals.map(v => parseFloat(v)))) },
        { label: "Annual Fee", fn: (p) => formatTT(p.annual_fee), best: (vals) => vals.indexOf(Math.min(...vals.map(v => parseFloat(v.replace(/[^\d]/g, ""))))) },
        { label: "Min Income", fn: (p) => formatTT(p.min_income) },
        { label: "Min Credit Score", fn: (p) => p.min_credit_score },
        { label: "Rating", fn: (p) => `${p.rating}/5` },
        { label: "Eligibility", fn: (p) => p.eligibility, multiline: true },
    ];

    return (
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-10">
            <div className="flex items-end justify-between mb-8">
                <div>
                    <div className="label-overline text-[#718096] mb-2">Side by Side</div>
                    <h1 className="font-serif-display text-4xl md:text-5xl tracking-tight">Comparison ({items.length})</h1>
                </div>
                <button onClick={clear} data-testid="compare-clear-btn" className="label-overline text-sm hover:underline">Clear all</button>
            </div>

            <div className="grid-card bg-white overflow-x-auto">
                <table className="w-full" data-testid="compare-table">
                    <thead>
                        <tr className="border-b border-[#0B192C]/10 sticky top-16 bg-white z-10">
                            <th className="w-48 text-left p-4 label-overline text-[#718096]">Attribute</th>
                            {items.map((p) => (
                                <th key={p.id} className="text-left p-4 align-top min-w-[220px]">
                                    <div className="flex items-start justify-between gap-2">
                                        <div>
                                            <div className="label-overline text-[#1E3A2B] mb-1">{p.provider}</div>
                                            <Link to={`/products/${p.id}`} className="font-serif-display text-xl leading-tight hover:underline decoration-[#D1A77E] underline-offset-4">
                                                {p.name}
                                            </Link>
                                        </div>
                                        <button
                                            onClick={() => remove(p.id)}
                                            data-testid={`compare-remove-${p.id}`}
                                            className="text-[#718096] hover:text-[#C53030]"
                                            aria-label="Remove"
                                        >
                                            <X className="w-4 h-4" strokeWidth={1.5}/>
                                        </button>
                                    </div>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row) => {
                            const vals = items.map(row.fn);
                            const bestIdx = row.best && items.length > 1 ? row.best(vals) : -1;
                            return (
                                <tr key={row.label} className="border-b border-[#0B192C]/5">
                                    <td className="p-4 label-overline text-[#718096] align-top">{row.label}</td>
                                    {vals.map((v, i) => (
                                        <td key={items[i].id} className={`p-4 font-mono-body text-sm align-top ${i === bestIdx ? "bg-[#1E3A2B]/5 font-medium" : ""} ${row.multiline ? "" : "whitespace-nowrap"}`}>
                                            {v}
                                            {i === bestIdx && <span className="ml-2 text-xs text-[#1E3A2B]">★ best</span>}
                                        </td>
                                    ))}
                                </tr>
                            );
                        })}
                        <tr>
                            <td className="p-4"></td>
                            {items.map((p) => (
                                <td key={p.id} className="p-4">
                                    <ApplyDialog product={p} trigger={
                                        <button data-testid={`compare-apply-${p.id}`} className="btn-accent w-full px-3 py-2 label-overline">Apply</button>
                                    } />
                                </td>
                            ))}
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default Compare;
